import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../components/ThemeProvider';
import './Home.css';

const Home: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  const pages = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      description: 'Main dashboard with balance overview, live markets, and FAQ section'
    },
    {
      name: 'Markets',
      path: '/markets',
      description: 'Market Scope page with search, filter tabs, and cryptocurrency trading pairs list'
    },
    {
      name: 'Assets',
      path: '/assets',
      description: 'Asset overview with total balance, action buttons, and portfolio distribution chart'
    },
    {
      name: 'Notifications',
      path: '/notifications',
      description: 'Notification center showing user alerts, updates, and activity'
    },
    {
      name: 'P2P Buy List',
      path: '/p2p-buy-list',
      description: 'P2P trading marketplace with buy/sell tabs, filters, and listing cards'
    },
    {
      name: 'P2P Buy View',
      path: '/p2p-buy-view',
      description: 'P2P buy transaction page with seller details, price info, and payment options'
    },
    {
      name: 'P2P Sell List',
      path: '/p2p-sell-list',
      description: 'P2P trading sell list page'
    },
    {
      name: 'P2P Sell View',
      path: '/p2p-sell-view',
      description: 'P2P sell order view page'
    },
    {
      name: 'P2P Trade Buy Pending Pay',
      path: '/p2p-trade-buy-pending-pay',
      description: 'P2P order created page with countdown timer, order details, and payment method information'
    },
    {
      name: 'P2P Trade Buy Waiting Seller',
      path: '/p2p-trade-buy-waiting-seller',
      description: 'P2P payment verification page showing waiting status while seller confirms the payment'
    },
    {
      name: 'P2P Trade Buy Complete',
      path: '/p2p-trade-buy-complete',
      description: 'P2P trade completion page showing successful transaction with order details and merchant payment info'
    },
    {
      name: 'P2P Trade Sell Waiting Buyer',
      path: '/p2p-trade-sell-waiting-buyer',
      description: 'P2P sell order page waiting for buyer to complete payment with countdown timer and merchant details'
    },
    {
      name: 'P2P Trade Sell Pending Release',
      path: '/p2p-trade-sell-pending-release',
      description: 'P2P sell order page verifying buyer payment with release crypto and dispute options'
    },
    {
      name: 'P2P Trade Sell Complete',
      path: '/p2p-trade-sell-complete',
      description: 'P2P sell trade completion page showing successful transaction with order details and payment method info'
    },
    {
      name: 'P2P Trade Chat',
      path: '/p2p-trade-chat',
      description: 'Real-time chat between buyer and trader for P2P trade support and communication'
    },
    {
      name: 'P2P Merchant Dashboard',
      path: '/p2p-merchant-dashboard',
      description: 'Comprehensive merchant dashboard with statistics, trades, and performance metrics'
    },
    {
      name: 'P2P Merchant Security Deposit',
      path: '/p2p-merchant-security-deposit',
      description: 'Merchant security deposit management page showing current deposit, required amount, progress, and transaction history'
    },
    {
      name: 'P2P Orders',
      path: '/p2p-orders',
      description: 'P2P orders management page with tabs for all, pending, completed, cancelled, and disputed orders'
    },
    {
      name: 'P2P Dispute',
      path: '/p2p-dispute',
      description: 'P2P dispute order page with form to report issues, select dispute reasons, and provide additional evidence'
    },
    {
      name: 'My Ads',
      path: '/my-ads',
      description: 'P2P merchant ads management page showing active advertisements with empty state and pagination'
    },
    {
      name: 'My Ads With Content',
      path: '/my-ads-with-content',
      description: 'P2P merchant ads management page showing active buy and sell advertisements with delete options'
    },
    {
      name: 'Post Ad Step 1',
      path: '/post-ad-step-1',
      description: 'P2P post ad step 1 page with trade type selection, cryptocurrency, payment methods, and time limit modals'
    },
    {
      name: 'Post Ad Step 2',
      path: '/post-ad-step-2',
      description: 'P2P post ad step 2 page with price and amount inputs, price type selection (Fixed/Market/Floating), and order limits'
    },
    {
      name: 'Post Ad Step 3',
      path: '/post-ad-step-3',
      description: 'P2P post ad step 3 (final) page with terms & conditions, auto-reply message, and ad preview modal'
    },
    {
      name: 'Post Ad Success',
      path: '/post-ad-success',
      description: 'P2P ad posting success confirmation page with notification banner and action buttons'
    },
    {
      name: 'Swap',
      path: '/swap',
      description: 'Cryptocurrency swap interface with from/to currency selection, network selection, and amount input'
    },
    {
      name: 'Swap Success',
      path: '/swap-success',
      description: 'Swap transaction success page with action buttons and transaction summary'
    },
    {
      name: 'Swap Details',
      path: '/swap-details',
      description: 'Detailed swap transaction view with copy functionality and transaction timeline'
    },
    {
      name: 'Withdraw',
      path: '/withdraw',
      description: 'Multi-crypto withdrawal interface with network selection, amount input, and fee options'
    },
    {
      name: 'Withdrawal Confirm',
      path: '/withdrawal-confirm',
      description: 'Withdrawal confirmation page showing transaction details, fees, and important notices before proceeding'
    },
    {
      name: 'Withdrawal Verify',
      path: '/withdrawal-verify',
      description: 'Two-factor authentication verification page with 6-digit OTP code input for withdrawal security'
    },
    {
      name: 'Withdrawal Success',
      path: '/withdrawal-success',
      description: 'Withdrawal success confirmation page with transaction details, status, and action buttons'
    },
    {
      name: 'Withdrawal Email Transfer',
      path: '/withdrawal-email-transfer',
      description: 'Email transfer withdrawal page for sending crypto to any user via email address'
    },
    {
      name: 'Withdrawal Verify 2FA',
      path: '/withdrawal-verify-2fa',
      description: 'Two-factor authentication page with OTP input for email transfer verification'
    },
    {
      name: 'Withdrawal Confirm Withdrawal',
      path: '/withdrawal-confirm-withdrawal',
      description: 'Transaction confirmation page showing amount, recipient, fees, and final receive amount'
    },
    {
      name: 'Deposit',
      path: '/deposit',
      description: 'Cryptocurrency deposit interface with QR code, address, and network selection'
    },
    {
      name: 'Deposit Details',
      path: '/deposit-details',
      description: 'Transaction details page showing deposit status, timeline, and transaction information'
    },
    {
      name: 'History Deposit',
      path: '/history-deposit',
      description: 'Transaction history page showing deposit records with filters for time and status'
    },
    {
      name: 'History Withdrawal',
      path: '/history-withdrawal',
      description: 'Transaction history page showing withdrawal records with filters for time and status'
    },
    {
      name: 'Profile',
      path: '/profile',
      description: 'User profile page with settings, security options, and account management'
    },
    {
      name: 'Profile Info',
      path: '/profile-info',
      description: 'Registration info page showing account limits, personal information, and referral code'
    },
    {
      name: 'Profile Security',
      path: '/profile-security',
      description: 'Security settings page with authentication methods and account management options'
    },
    {
      name: 'Disable 2FA',
      path: '/disable-2fa',
      description: 'Disable two-factor authentication by providing your password'
    },
    {
      name: 'Enable 2FA',
      path: '/enable-2fa',
      description: 'Setup two-factor authentication with QR code and secret key'
    },
    {
      name: 'Profile Fullname',
      path: '/profile-fullname',
      description: 'Edit full name page with input field and save functionality'
    },
    {
      name: 'Profile Phone Number',
      path: '/profile-phone-number',
      description: 'Edit phone number page with input field and country code selection'
    },
    {
      name: 'Change Password',
      path: '/change-password',
      description: 'Change password page with current password and new password fields'
    },
    {
      name: 'Profile Manage Account',
      path: '/profile-manage-account',
      description: 'Account management page with deactivate and delete account options'
    },
    {
      name: 'Profile Refer and Earn',
      path: '/profile-refer-and-earn',
      description: 'Referral program page with referral link, code, statistics, and referrals list'
    },
    {
      name: 'Profile P2P Payment Methods',
      path: '/profile-p2p-payment-methods',
      description: 'P2P payment methods management page with bank account details and add/edit/delete options'
    },
    {
      name: 'Sign In',
      path: '/login',
      description: 'User login page with email/phone and password fields'
    },
    {
      name: 'Sign Up',
      path: '/sign-up',
      description: 'User registration page with all required fields and password validation'
    },
    {
      name: 'Forgot Password',
      path: '/forgot-password',
      description: 'Password reset page to send reset instructions via email or phone'
    },
    {
      name: 'Verify Password',
      path: '/verify-password',
      description: 'Email/Phone number verification page with 6-digit OTP input'
    },
    {
      name: 'Password Reset',
      path: '/password-reset',
      description: 'Reset password with OTP verification and new password creation'
    },
    {
      name: 'Password Reset Success',
      path: '/password-reset-success',
      description: 'Success confirmation screen after password reset'
    }
  ];

  return (
    <div className="home-container">
      <div className="home-content">
        <header className="home-header">
          <h1 className="home-title">Coinsher App Theme</h1>
          <p className="home-subtitle">
            A React-based theme template showcasing UI components for a React Native application
          </p>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </header>

        <div className="pages-section">
          <h2 className="section-title">Available Pages</h2>
          <div className="pages-grid">
            {pages.map((page) => (
              <Link
                key={page.path}
                to={page.path}
                className="page-card"
              >
                <div className="page-card-content">
                  <h3 className="page-name">{page.name}</h3>
                  <p className="page-description">{page.description}</p>
                  <span className="page-link-text">
                    View Page →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="info-section">
          <div className="info-card">
            <h3 className="info-title">🎨 Design System</h3>
            <p className="info-text">
              Centralized CSS design system with support for light and dark themes
            </p>
          </div>
          <div className="info-card">
            <h3 className="info-title">📱 Mobile-First</h3>
            <p className="info-text">
              All components are designed with mobile viewport in mind
            </p>
          </div>
          <div className="info-card">
            <h3 className="info-title">🔧 Mock Data</h3>
            <p className="info-text">
              Self-contained with no external API dependencies
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;